import { useMemo } from "react";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { useHierarchyRelations } from "../../../../hooks/useHierarchyRelations";
import { SAMConfig } from "../../../../types/workspace";
import { transformToTree } from "../../../../utils/filters/utils";
import { FilterLabel, IndustryNameData } from "../types";
import { getLabelText, getTotalCounts } from "../utils";

function mapIdsToNames<T extends string | number>(
  ids: T[],
  nameMap: Map<T, string>
): string[] {
  return ids.map((id) => nameMap.get(id)).filter(Boolean) as string[];
}

export const useIndustryData = (config?: SAMConfig["industry"]) => {
  const {
    sic: sics,
    naics,
    sicNameMap,
    naicsNameMap,
    hgIndustryNameMap,
  } = useTaxonomyContext();

  // Transform SICs and NAICS to tree structure for hierarchy relations
  const sicTree = transformToTree(sics, (sic) => sic.name);
  const naicsTree = transformToTree(naics, (naics) => naics.name);

  // Use hierarchy relations for SICs and NAICS
  const { filterToHighestAncestors: filterSicsToHighestAncestors } =
    useHierarchyRelations(sicTree);
  const { filterToHighestAncestors: filterNaicsToHighestAncestors } =
    useHierarchyRelations(naicsTree);

  const filteredSelectedSicIds = filterSicsToHighestAncestors(
    config?.sicIds || []
  );
  const filteredExcludedSicIds = filterSicsToHighestAncestors(
    config?.excludedSicIds || []
  );

  const filteredSelectedNaicsIds = filterNaicsToHighestAncestors(
    config?.naicsIds || []
  );
  const filteredExcludedNaicsIds = filterNaicsToHighestAncestors(
    config?.excludedNaicsIds || []
  );

  const selectedSicNames = useMemo(
    () => mapIdsToNames(filteredSelectedSicIds, sicNameMap),
    [filteredSelectedSicIds, sicNameMap]
  );

  const excludedSicNames = useMemo(
    () => mapIdsToNames(filteredExcludedSicIds, sicNameMap),
    [filteredExcludedSicIds, sicNameMap]
  );

  const selectedNaicsNames = useMemo(
    () => mapIdsToNames(filteredSelectedNaicsIds, naicsNameMap),
    [filteredSelectedNaicsIds, naicsNameMap]
  );

  const excludedNaicsNames = useMemo(
    () => mapIdsToNames(filteredExcludedNaicsIds, naicsNameMap),
    [filteredExcludedNaicsIds, naicsNameMap]
  );

  const selectedHgIndustryNames = useMemo(
    () => mapIdsToNames(config?.ids || [], hgIndustryNameMap),
    [config?.ids, hgIndustryNameMap]
  );

  const excludedHgIndustryNames = useMemo(
    () => mapIdsToNames(config?.excludedIds || [], hgIndustryNameMap),
    [config?.excludedIds, hgIndustryNameMap]
  );

  const industryData = useMemo<IndustryNameData>(() => {
    return {
      hgData: {
        included: selectedHgIndustryNames,
        excluded: excludedHgIndustryNames,
      },
      sicData: {
        included: selectedSicNames,
        excluded: excludedSicNames,
      },
      naicsData: {
        included: selectedNaicsNames,
        excluded: excludedNaicsNames,
      },
    };
  }, [
    selectedHgIndustryNames,
    excludedHgIndustryNames,
    selectedSicNames,
    excludedSicNames,
    selectedNaicsNames,
    excludedNaicsNames,
  ]);

  const labels = useMemo((): FilterLabel[] => {
    const { includedCount, excludedCount } = getTotalCounts(industryData);
    const labelText = getLabelText("Industries", includedCount, excludedCount);
    return labelText ? [{ text: labelText, type: "industry" }] : [];
  }, [industryData]);

  return {
    industryData,
    isLoading: false,
    labels,
  };
};
